import { useState, Fragment, useEffect } from 'react';
import { useRouter } from 'next/router';
import { useSnackbar } from 'notistack';
// @mui
import {
  Stack,
  Typography,
  Card,
  Box,
} from '@mui/material';
import { alpha } from '@mui/material/styles';
// components
import Iconify from '../../../components/Iconify';
import SvgIconStyle from '../../../components/SvgIconStyle';
// hooks
import useResponsive from '../../../hooks/useResponsive';
import useAuth from '../../../hooks/useAuth';
// sections
import {
  MeetingAgainDialog,
  ExtendDeadlineDialog,
} from './';
// utils
import { fKrSendKST } from '../../../utils/formatTime';
import axios from '../../../utils/axios';

// ----------------------------------------------------------------------

export default function BeforeNegotiationCard({
  jobPost,
  candidate,
  loadData,
  openExtendDialog,
}) {
  const [openMeetingAgainDialog, setOpenMeetingAgainDialog] = useState(false);
  const [openCrewNegoExtendDialog, setOpenCrewNegoExtendDialog] = useState(false);
  const negoExtendable = (candidate?.status === "crew-negotiate-to-company" || candidate?.status === "company-negotiate-rewrite") && candidate.nego_extendable;
  const crewNegoExtendable = candidate?.status == "waiting-offer-from-crew" && candidate?.crew_nego_deadline && !candidate?.crew_deadline_extend_at;
  const router = useRouter();
  const { extend, waiting } = router.query;
  const { user } = useAuth();
  const { enqueueSnackbar } = useSnackbar();

  const updateWaitingCrewNego = async () => {
    const companyId = user?.employee.company[0].id;
    const jobPostId = jobPost?.id;
    const candidateId = candidate?.id;
    await axios.post(`/api/company/${companyId}/job-posts/${jobPostId}/candidate/${candidateId}/waiting-crew-nego/`);
    enqueueSnackbar('크루의 제안을 기다리는 중입니다');
  }

  useEffect(() => {
    if (extend === "y" && crewNegoExtendable) {
      setOpenCrewNegoExtendDialog(true);
    }
  }, [extend]);

  useEffect(() => {
    if (waiting === "y" && candidate?.status == "waiting-offer-from-crew") {
      updateWaitingCrewNego();
    }
  }, [waiting]);

  return (
    <Fragment>
    {candidate?.status === "waiting-offer-from-crew" && candidate?.crew_nego_deadline && (
      <Box
        sx={{
          px: 2.5,
          py: 2,
          borderRadius: 1.5,
          bgcolor: (theme) => alpha(theme.palette.error.main, 0.08),
          mb: 2,
        }}
      >
        <Typography variant="body2">
          <b>{fKrSendKST(candidate?.crew_nego_deadline)}</b>까지 크루가 계약 조건을 작성하지 않으면 매칭이 중단됩니다.
        </Typography>
      </Box>
    )}
    <Stack sx={{ mb: 3 }} spacing={{ xs: 1.5, sm: 3 }} direction="row" alignItems={"stretch"} justifyContent={"center"}>
      {crewNegoExtendable && (
        <ButtonCard
          onClick={() => setOpenCrewNegoExtendDialog(true)}
          title="크루의 제안 전에 시간이 더 필요하신가요?"
          titleIcon={"/icons/ic_clock.svg"}
          subTitle="기한 연장하기"
        />
      )}
      {negoExtendable && (
        <ButtonCard
          onClick={openExtendDialog}
          title="결정할 시간이 더 필요하신가요?"
          titleIcon={"/icons/ic_clock.svg"}
          subTitle="기한 연장하기"
        />
      )}
      {!candidate?.coffee_chat &&
        <ButtonCard
          onClick={() => setOpenMeetingAgainDialog(true)}
          title="미팅을 한번 더 하기로 했나요?"
          titleIcon={"/icons/ic_videocam.svg"}
          subTitle="재미팅 요청하기"
        />
      }
      {/* spacing */}
      {!negoExtendable && !crewNegoExtendable && ( <Box sx={{ width: 1 }} /> )}
    </Stack>
    <MeetingAgainDialog
      jobPostId={jobPost?.id}
      candidateId={candidate?.id}
      isOpen={openMeetingAgainDialog}
      closeDialog={() => setOpenMeetingAgainDialog(false)}
      meetingCount={candidate?.meeting_count}
      loadData={loadData}
    />
    <ExtendDeadlineDialog
      currentDeadline={candidate?.crew_nego_deadline}
      isOpen={openCrewNegoExtendDialog}
      closeDialog={() => setOpenCrewNegoExtendDialog(false)}
      reloadOffer={loadData}
      jobPost={jobPost}
      candidate={candidate}
    />
    </Fragment>
  )
};


function ButtonCard({ onClick, title, titleIcon, subTitle }) {
  const smUp = useResponsive('up', 'sm');
  return (
    <Card
      sx={{
        width: 1,
        boxShadow: "none",
        border: "1px solid",
        borderColor: "divider",
        backgroundColor: "grey.100",
        px: { xs: 2, sm: 3 },
        py: { xs: 2, sm : 2.5},
        ":hover": { cursor: "pointer", backgroundColor: "grey.200" },
        ":active": { backgroundColor: "grey.300" },
      }}
      onClick={onClick}
    >
      <Stack spacing={1} justifyContent={"space-between"} height={"100%"}>
        <Stack direction="row" alignItems={smUp ? "center" : "flex-start"} spacing={1} justifyContent={"space-between"}>
          <Typography variant={smUp ? "subtitle1" : "subtitle2"} sx={{ wordBreak: "keep-all" }}>
            {title}
          </Typography>
          <SvgIconStyle
            src={titleIcon}
            sx={{
              minWidth: smUp ? 24 : 20,
              minHeight: smUp ? 24 : 20,
              color: "text.disabled",
            }}
          />
        </Stack>
        <Stack direction="row" alignItems="center" spacing={0.5}>
          <Typography variant={smUp ? "subtitle2" : "overline"} sx={{ color: "text.secondary" }}>
            {subTitle}
          </Typography>
          <Iconify icon="ic:chevron-right" sx={{ color: "text.secondary", width: smUp ? 20 : 16, height: smUp ? 20 : 16 }} />
        </Stack>
      </Stack>
    </Card>
  );
}
